﻿import { BaseCdnApi } from './BaseCdnApi'
import { FetchFromCdnOptions } from './CdnApi'
import { RundotGameAPI } from '../rundot-game-api/types'

/**
 * CDN API implementation for mock/development environments.
 * Serves assets from the public/cdn-assets folder via Vite dev server.
 */
export class MockCdnApi extends BaseCdnApi {
  private static readonly LEGACY_CDN_PREFIX = 'cdn/'
  private static readonly SHARED_ASSETS_CDN_BASE =
    'https://venus-static-01293ak.web.app'

  private readonly rundotGameApi: RundotGameAPI

  constructor(rundotGameApi: RundotGameAPI) {
    super()
    this.rundotGameApi = rundotGameApi
  }

  protected getCdnAssetsBaseUrl(): string {
    // In mock mode, cdn-assets are served from the public folder via relative path
    // Vite dev server serves public folder contents at the root
    return 'cdn-assets'
  }

  protected getCdnBaseUrl(): string {
    return MockCdnApi.SHARED_ASSETS_CDN_BASE
  }

  protected getSharedAssetsCdnBaseUrl(): string {
    // In mock mode, shared assets are served from the public folder root
    return ''
  }

  async resolveAssetUrl(subPath: string): Promise<string> {
    // If subPath is already a full URL, return it as-is (useful for testing)
    if (this.isFullUrl(subPath)) {
      return subPath
    }

    // If already prefixed with cdn/, return as-is (for legacy compatibility)
    if (subPath.startsWith(MockCdnApi.LEGACY_CDN_PREFIX)) {
      return subPath
    }

    // Build URL using base method (cdn-assets/path/to/file?cacheBust=...)
    return this.buildCdnUrl(subPath, this.getCdnAssetsBaseUrl())
  }

  /**
   * Fetch an asset directly from the bucket root (public folder root in mock mode).
   * This mimics production behavior where cross-game assets are at the bucket root.
   *
   * @param subPath - The path relative to public folder root (e.g., "othergame/bundle.stow")
   * @param options - Optional fetch options including timeout
   * @returns Promise<Blob> - The asset data as a Blob
   *
   * @example
   * // In mock mode with public folder structure:
   * // fetchFromCdn("othergame/bundle.stow")
   * // → othergame/bundle.stow (from public root, not public/cdn-assets)
   */
  async fetchFromCdn(
    subPath: string,
    options?: FetchFromCdnOptions,
  ): Promise<Blob> {
    // Fetch from public root, not cdn-assets subfolder
    const cleanSubPath = subPath.startsWith('/') ? subPath.slice(1) : subPath
    const encodedSubPath = this.encodePathParts(cleanSubPath)
    return this.executeFetch(encodedSubPath, options)
  }

  /**
   * Fetch an asset in mock/development mode.
   * In development, assets are served directly without manifest transformation,
   * so this method fetches from the cdn-assets folder.
   *
   * @param relativePath - The path to the asset (e.g., "/some/asset.png" or "some/asset.png")
   * @param options - Optional fetch options including timeout
   * @returns Promise<Blob> - The asset data as a Blob
   */
  async fetchAsset(
    relativePath: string,
    options?: FetchFromCdnOptions,
  ): Promise<Blob> {
    // In mock mode, no manifest transformation is needed
    // Build path to cdn-assets folder: cdn-assets/{relativePath}
    const normalizedPath = relativePath.startsWith('/')
      ? relativePath.slice(1)
      : relativePath
    const fullPath = `cdn-assets/${normalizedPath}`
    return this.fetchFromCdn(fullPath, options)
  }
}
